import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { GatsbyImage } from 'gatsby-plugin-image';
import { isValidElement, ReactNode } from 'react';
import { A11y, Autoplay, EffectFade, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { NavigationButtons, SwiperCarousel } from 'components/rebrand/SwiperCarousel';

import { ISanityImgWithAltText } from 'typescriptDefs';

import { useIsMobileSize } from 'helpers';

import { H2 } from './Typography';

type Props = {
  alignCenter?: boolean;
  content: {
    description: PortableTextBlock | PortableTextBlock[] | JSX.Element;
    heading: PortableTextBlock | PortableTextBlock[] | undefined;
    image: ISanityImgWithAltText;
  }[];
  cta?: ReactNode;
  heading?: PortableTextBlock | PortableTextBlock[] | string | JSX.Element;
  name: string;
  navigationBottom?: boolean;
  variant: 'primaryDark' | 'dangerLight';
  loop?: boolean;
  autoPlay?: boolean | AutoplayOptions | undefined;
};

const VerticalSwiper = ({
  alignCenter = false,
  cta = null,
  content,
  heading,
  name,
  navigationBottom = false,
  variant,
  loop = false,
  autoPlay = false,
}: Props) => {
  const paginationClass = `${name}-pagination`;
  const navNextClass = `${name}-next`;
  const navPrevClass = `${name}-prev`;
  const isMobile = useIsMobileSize();

  return (
    <Section
      className={twMerge(
        clsx(
          'py-56 md:py-80',
          variant === 'dangerLight' && 'bg-danger-50',
          variant === 'primaryDark' && 'bg-primary-500',
        ),
      )}
    >
      <Container className="px-32 xs:px-0 md:flex md:flex-row md:gap-32 lg:gap-60">
        {isMobile ? (
          <div>
            <div
              className={twMerge(
                clsx(
                  'mb-40 [&_*]:text-[36px]',
                  variant === 'dangerLight' && 'text-primary-400 [&_strong]:text-primary-300',
                  variant === 'primaryDark' && 'text-white [&_strong]:text-primary-50',
                ),
              )}
            >
              {typeof heading === 'string' ? (
                <H2>{heading}</H2>
              ) : isValidElement(heading) ? (
                heading
              ) : (
                <CustomPortableText content={heading as PortableTextBlock[]} />
              )}
            </div>
            {!navigationBottom && (
              <NavigationButtons
                className="mb-32"
                nextNavEl={navNextClass}
                prevNavEl={navPrevClass}
                buttonVariant={variant === 'dangerLight' ? 'primary' : 'primaryInverse'}
              />
            )}
            <SwiperCarousel
              modules={[A11y, Keyboard, Navigation, Autoplay]}
              noNavigation
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 16 },
              }}
              slidesOffsetAfter={0}
              nextNavEl={navNextClass}
              prevNavEl={navPrevClass}
              loop={loop}
              autoplay={autoPlay}
            >
              {content.map((feature, index) => (
                <SwiperSlide key={index}>
                  <div
                    className={twMerge(
                      clsx(
                        'flex flex-col items-center rounded-16 p-32',
                        variant === 'dangerLight' && 'bg-primary-50',
                        variant === 'primaryDark' && 'bg-black bg-opacity-20',
                      ),
                    )}
                  >
                    <GatsbyImage
                      className="mb-32 aspect-[0.9] max-h-400 rounded-8 md:rounded-16"
                      image={feature.image.img.asset.gatsbyImageData}
                      alt={feature.image.altText}
                    />
                    <div
                      className={twMerge(
                        clsx(
                          'mt-auto [&_h3]:mb-16 [&_h3]:text-3xl md:[&_h3]:text-3xl',
                          '[&_h4]:mb-8 [&_h4]:text-xl [&_h4]:font-bold md:[&_h4]:text-xl',
                          variant === 'dangerLight' && '[&_h3]:text-primary-400',
                          variant === 'primaryDark' && 'text-white [&_*]:text-white',
                        ),
                      )}
                    >
                      {feature.heading && (
                        <CustomPortableText
                          headingStyles={{ h3: { headingStyle: true }, h4: { headingStyle: true } }}
                          content={feature.heading}
                        />
                      )}
                      {isValidElement(feature.description) ? (
                        feature.description
                      ) : (
                        <CustomPortableText
                          headingStyles={{ h3: { headingStyle: true }, h4: { headingStyle: true } }}
                          noCheckmarkList
                          content={feature.description as PortableTextBlock[]}
                        />
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </SwiperCarousel>
            {navigationBottom && (
              <NavigationButtons
                className="mt-32"
                nextNavEl={navNextClass}
                prevNavEl={navPrevClass}
                buttonVariant={variant === 'dangerLight' ? 'primary' : 'primaryInverse'}
              />
            )}
          </div>
        ) : (
          <SwiperCarousel
            direction="vertical"
            modules={[A11y, Keyboard, Pagination, EffectFade, Autoplay]}
            noNavigation
            breakpoints={{
              0: { slidesPerView: 1, spaceBetween: 0 },
            }}
            slidesOffsetAfter={0}
            pagination={{
              clickable: true,
              el: `.${paginationClass}`,
            }}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            className="h-520 [&_.swiper]:h-full [&_.swiper]:w-full"
            loop={loop}
            autoplay={autoPlay}
          >
            {content.map((feature, index) => (
              <SwiperSlide key={index}>
                <div key={index} className="grid h-auto grid-cols-2 gap-80 lg:gap-130">
                  <div className={twMerge(clsx('flex flex-col', alignCenter && 'justify-center'))}>
                    <div
                      className={twMerge(
                        clsx(
                          'md:w-[90%] [&_*]:text-[36px] md:[&_*]:text-8xl',
                          variant === 'dangerLight' && 'text-primary-400 [&_strong]:text-primary-300',
                          variant === 'primaryDark' && 'text-white [&_*]:text-white [&_strong]:text-primary-50',
                        ),
                      )}
                    >
                      {typeof heading === 'string' ? (
                        <H2>{heading}</H2>
                      ) : isValidElement(heading) ? (
                        heading
                      ) : (
                        <CustomPortableText content={heading as PortableTextBlock[]} />
                      )}
                    </div>
                    <div
                      className={twMerge(
                        clsx(
                          'mt-48 w-4/5 [&_h3]:mb-16 [&_h3]:font-basel-grotesk [&_h3]:text-3xl [&_h3]:normal-case md:[&_h3]:text-3xl',
                          '[&_h4]:mb-8 [&_h4]:text-xl md:[&_h4]:text-xl',
                          !alignCenter && 'mt-auto',
                          variant === 'dangerLight' && '[&_h3]:text-primary-400 [&_h4]:text-primary-400',
                          variant === 'primaryDark' && 'text-white [&_h3]:text-white [&_h4]:text-white',
                        ),
                      )}
                    >
                      {feature.heading && <CustomPortableText content={feature.heading} />}
                      {isValidElement(feature.description) ? (
                        feature.description
                      ) : (
                        <CustomPortableText content={feature.description as PortableTextBlock[]} />
                      )}
                      {cta}
                    </div>
                  </div>
                  <GatsbyImage
                    className="rounded-16 md:max-h-517"
                    image={feature.image.img.asset.gatsbyImageData}
                    alt={feature.image.altText}
                  />
                </div>
              </SwiperSlide>
            ))}
          </SwiperCarousel>
        )}
        <div className="relative hidden h-auto md:block">
          <div
            className={twMerge(
              paginationClass,
              '!relative !left-0 !right-0 my-auto flex flex-col items-center gap-12 rounded-full bg-opacity-10 p-8',
              variant === 'dangerLight' && 'bg-black',
              variant === 'primaryDark' && 'bg-white',
              '[&_.swiper-pagination-bullet]:!m-0 [&_.swiper-pagination-bullet]:h-8 [&_.swiper-pagination-bullet]:w-8 [&_.swiper-pagination-bullet]:bg-white [&_.swiper-pagination-bullet]:opacity-100',
              '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:h-12 [&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:w-12',
              '[&_.swiper-pagination-bullet]:transition-colors',
              variant === 'dangerLight' &&
                '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:bg-primary-400 hover:[&_.swiper-pagination-bullet]:bg-white focus:hover:[&_.swiper-pagination-bullet]:bg-primary-500',
              variant === 'primaryDark' &&
                '[&_.swiper-pagination-bullet.swiper-pagination-bullet-active]:bg-secondary-75 hover:[&_.swiper-pagination-bullet]:bg-secondary-100 focus:hover:[&_.swiper-pagination-bullet]:bg-secondary-100',
            )}
          />
        </div>
      </Container>
    </Section>
  );
};

export default VerticalSwiper;
